import React from 'react';
import styled from '@emotion/styled';
import { Box, type BoxProps } from '@mui/material';
import { Text } from 'components/text';
import { Select } from 'components/inputs/Select';
import { TextInput } from 'components/inputs';

export const headerContainerBaseSx: BoxProps['sx'] = {
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  gap: '24px',
};

export const HeaderContainer = styled(Box)`
  @media (max-width: 768px) {
    flex-direction: column;
  }
` as React.ComponentType<BoxProps>;

export const SectionContainer = styled(Box)<{ width?: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: ${props => props.width || 100}%;

  @media (max-width: 768px) {
    width: 100% !important;
  }
` as React.ComponentType<BoxProps & { width?: number }>;

export const Divider = styled(Box)`
  width: 0.5px;
  background-color: ${p => p.theme.palette.border.primarySubtle};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 0.5px;
  }
` as React.ComponentType<BoxProps>;

export const SectionHeader = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
` as React.ComponentType<BoxProps>;

export const FieldsContainer = styled(Box)<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns}, minmax(0, 1fr));

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
` as React.ComponentType<BoxProps & { columns: number }>;

export const baseFieldContainerSx: BoxProps['sx'] = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  minWidth: 0,
};

export const FieldContainer = styled(Box)`` as React.ComponentType<BoxProps>;

export const FieldLabel = styled(Text)`
  /* Label styling is handled by Text component */
`;

export const FieldValue = styled(Text)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--Text-Primary-text-primary, #19191b);
  text-overflow: ellipsis;
`;

export const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const IndicatorWrapper = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 4px;
`;

export const ActionButtons = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
` as React.ComponentType<BoxProps>;

export const CancelLink = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
  color: ${p => p.theme.palette.text.secondary};
  font-size: 10.5px;
  font-weight: ${p => p.theme.typography.weight.regular};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  &:active {
    opacity: 0.5;
  }

  svg {
    color: ${p => p.theme.palette.text.secondary};
  }
`;

export const SaveLink = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
  color: ${p => p.theme.palette.success.main};
  font-size: 10.5px;
  font-weight: ${p => p.theme.typography.weight.regular};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    opacity: 0.6;
  }

  svg {
    color: inherit;
    fill: currentColor;
  }
`;

export const FieldInputWrapper = styled(Box)`` as React.ComponentType<BoxProps>;

export type CustomDropdownWrapperProps = {
  width?: string | number;
  height?: string | number;
  flexible?: boolean;
  children: React.ReactNode;
  sx?: BoxProps['sx'];
};

export const PAGE_HEADER_SELECT_HEIGHT_PX = 25;
export const PAGE_HEADER_SELECTED_VALUE_COLOR = 'var(--Text-Primary-text-primary, #19191b)';

export const CustomDropdownWrapper = ({
  width,
  height,
  flexible = false,
  children,
  sx,
}: CustomDropdownWrapperProps) => {
  const heightCss =
    height != null
      ? typeof height === 'number'
        ? `${height}px`
        : height
      : `${PAGE_HEADER_SELECT_HEIGHT_PX}px`;
  return (
    <Box
      sx={theme => ({
        width: width != null ? (typeof width === 'number' ? `${width}px` : width) : '100%',
        height: heightCss,
        minHeight: heightCss,
        ...(flexible && { minWidth: 0, maxWidth: '100%' }),
        '& .MuiFormControl-root': { margin: 0 },
        '& [data-testid="custom_dropdown_trigger"]': {
          minHeight: heightCss,
          height: heightCss,
          fontSize: theme.typography.bodySm.fontSize,
          ...(flexible && {
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }),
        },
        '& [data-testid="custom_dropdown_trigger"] p, & [data-testid="custom_dropdown_trigger"] span':
          { fontSize: theme.typography.bodySm.fontSize },
        '& [data-testid="custom_dropdown_trigger"][data-has-value] p, & [data-testid="custom_dropdown_trigger"][data-has-value] span':
          { color: PAGE_HEADER_SELECTED_VALUE_COLOR },
        ...(sx as object),
      })}
    >
      {children}
    </Box>
  );
};

export const SwitchWrapper = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;

  & .MuiSwitch-root {
    margin: 0 !important;
  }
` as React.ComponentType<BoxProps>;

export const SwitchValue = styled(Text)`
  /* Text styling is handled by Text component */
`;

export const StyledSelect = styled(Select)`
  width: 100%;

  & .MuiInputBase-root {
    background-color: #f2f2f3 !important;
    border: none;
    border-radius: 4px;
    padding: 6px 8px;
    min-height: 25px;
    height: 25px;
    display: flex;
    align-items: center;
    font-size: ${p => p.theme.typography.bodySm.fontSize};

    &::before,
    &::after {
      display: none;
    }

    &:hover,
    &.Mui-focused {
      background-color: #f2f2f3 !important;
    }
  }

  & .MuiSelect-root {
    background-color: #f2f2f3 !important;

    &:hover,
    &.Mui-focused {
      background-color: #f2f2f3 !important;
    }
  }

  & .MuiSelect-select {
    padding: 0;
    min-height: auto;
    height: auto;
    display: flex;
    align-items: center;
    background-color: transparent !important;
    font-size: ${p => p.theme.typography.bodySm.fontSize} !important;
    color: var(--Text-Primary-text-primary, #19191b);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    & .MuiBox-root {
      align-items: center;
      font-size: ${p => p.theme.typography.bodySm.fontSize} !important;
    }

    & p,
    & span {
      font-size: ${p => p.theme.typography.bodySm.fontSize} !important;
    }
  }

  & .MuiSelect-icon {
    right: 8px;
    top: 2px;
  }
`;

/** Compact trigger style for CustomDropDown in page header */
export const pageHeaderDropDownTriggerSx = {
  padding: '6px 8px',
  borderRadius: '4px',
  backgroundColor: '#f2f2f3',
  border: 'none',
  justifyContent: 'space-between',
  '&:hover': { backgroundColor: '#f2f2f3' },
  '& .MuiButton-startIcon': { marginRight: 1 },
  '& .MuiButton-endIcon': { marginLeft: 1 },
};

export const StyledTextInput = styled(TextInput)`
  & .MuiInput-root {
    background-color: #f2f2f3;
    border: none;
    border-radius: 4px;
    padding: 6px 8px;
    min-height: 25px;
    height: 25px;
    display: flex;
    align-items: center;
    font-size: ${p => p.theme.typography.bodySm.fontSize};

    &::before,
    &::after {
      display: none;
    }

    &:hover,
    &.Mui-focused {
      background-color: #f2f2f3;
    }
  }

  & .MuiInput-input {
    padding: 0;
    min-height: auto;
    height: auto;
    font-size: ${p => p.theme.typography.bodySm.fontSize};
    color: var(--Text-Primary-text-primary, #19191b);
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`;
